import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Target, Calendar, CheckCircle2, Zap, Building2, Settings, ChevronDown, Check, Search } from 'lucide-react';
import { Plan } from '../../models/plan';
import { PlanCardSelector } from './PlanCardSelector';
import { UserPlanSummary } from '../../services/usage.service';
import { formatDate } from '../../utils/date';
import { PLAN_METERS } from '../../utils/plan.utils';
import type { PlanLimits, PlanMeter } from '../../types/plan.types';
import { Button } from '@/components/ui/atoms/Button';
import { Typography } from './atoms/Typography/Typography';
import { Input as DSInput } from './atoms/Input';
import {
  type WorkspaceMode,
  type PlanType,
  type InvitationFormState,
  type ValidationResult,
  validateInvitationForm,
  buildInvitationPayload,
} from './planAssignmentModal.utils';

export type { PlanType } from './planAssignmentModal.utils';

export interface PlanFormData {
  planType: PlanType;
  fixedPlanCode: string;
  /** Per-meter limits; -1 means unlimited. */
  limits: PlanLimits;
  expiresAt: string;
  tenantId?: string | null;
}

const EMPTY_LIMITS: PlanLimits = { cv: 0, interview: 0, job: 0, talent_search: 0, talent_unlock: 0 };
const NO_UNLIMITED: Record<PlanMeter, boolean> = {
  cv: false,
  interview: false,
  job: false,
  talent_search: false,
  talent_unlock: false,
};

export type PlanAssignmentMode = 'create_invitation' | 'update_plan';

interface PlanAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: PlanAssignmentMode;
  email?: string;
  currentPlan?: UserPlanSummary | null;
  userId?: string;
  plans: Plan[];
  tenants?: { id: string; name: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (data: any) => Promise<void>;
  isSubmitting?: boolean;
  userName?: string;
  userEmail?: string;
}

function SearchableWorkspaceSelect({
  tenants,
  value,
  onChange,
  disabled,
  error,
}: {
  tenants: { id: string; name: string }[];
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  error?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const filtered = useMemo(
    () =>
      search.trim()
        ? tenants.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
        : tenants,
    [tenants, search]
  );

  const selectedTenant = tenants.find((t) => t.id === value);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    if (
      triggerRef.current && !triggerRef.current.contains(target) &&
      dropdownRef.current && !dropdownRef.current.contains(target)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClickOutside, updatePosition]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="space-y-0.5">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => { setIsOpen(!isOpen); setSearch(''); }}
        className={`flex h-9 w-full items-center justify-between rounded-lg border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${
          error ? 'border-red-400' : 'border-dark-invirtus/10 focus:ring-gradient-purple focus:border-transparent'
        }`}
      >
        <span className={selectedTenant ? 'text-foreground' : 'text-muted-foreground'}>
          {selectedTenant?.name ?? 'Select a workspace'}
        </span>
        <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="z-[100] rounded-md border bg-white shadow-md">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              className="w-full text-sm outline-none placeholder:text-gray-400"
              placeholder="Search workspaces..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="max-h-48 overflow-y-auto overscroll-contain p-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">No workspaces found</li>
            ) : (
              filtered.map((t) => (
                <li
                  key={t.id}
                  className={`relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm hover:bg-accent hover:text-accent-foreground ${
                    t.id === value ? 'bg-accent/50' : ''
                  }`}
                  onClick={() => {
                    onChange(t.id);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  {t.id === value && (
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                  {t.name}
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body
      )}

      {error && (
        <p className="text-xs text-red-600" role="alert">{error}</p>
      )}
    </div>
  );
}

const WORKSPACE_HINTS: Record<WorkspaceMode, string> = {
  new_workspace:
    'A dedicated workspace will be automatically created for this user. Select a plan to define their usage limits.',
  existing_workspace:
    'This user will be added to the selected workspace and inherit its active plan.',
  custom_workspace:
    "Enter a name to create a new workspace for this user. You'll also assign a plan to activate it.",
};

export const PlanAssignmentModal: React.FC<PlanAssignmentModalProps> = ({
  isOpen,
  onClose,
  mode,
  email: initialEmail = '',
  currentPlan,
  userId,
  plans,
  onSubmit,
  isSubmitting = false,
  userName,
  userEmail,
  tenants,
}) => {
  const [planType, setPlanType] = useState<PlanType>('fixed');
  const [fixedPlanCode, setFixedPlanCode] = useState<string>('');
  const [limits, setLimits] = useState<PlanLimits>(EMPTY_LIMITS);
  const [unlimited, setUnlimited] = useState<Record<PlanMeter, boolean>>(NO_UNLIMITED);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [email, setEmail] = useState<string>(initialEmail);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>('new_workspace');
  const [tenantName, setTenantName] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<ValidationResult['errors']>({});

  // Initialize form data based on current plan (update_plan mode)
  useEffect(() => {
    if (!isOpen) return;
    
    if (mode === 'update_plan' && currentPlan) {
      // Determine plan type from current plan
      if (currentPlan.plan_kind === 'fixed') {
        setPlanType('fixed');
        if (currentPlan.fixed_plan_code) {
          setFixedPlanCode(currentPlan.fixed_plan_code);
        }
      } else if (currentPlan.plan_kind === 'payg') {
        setPlanType('payg');
      } else {
        setPlanType('enterprise');
      }
      
      const nextLimits = { ...EMPTY_LIMITS };
      const nextUnlimited = { ...NO_UNLIMITED };
      for (const { key } of PLAN_METERS) {
        const value = currentPlan.limits?.[key];
        if (value === null || value === undefined || value === -1) {
          nextUnlimited[key] = true;
        } else {
          nextLimits[key] = value;
        }
      }
      setLimits(nextLimits);
      setUnlimited(nextUnlimited);
      
      // Set expiration date
      if (currentPlan.expires_at) {
        const date = new Date(currentPlan.expires_at);
        setExpiresAt(date.toISOString().split('T')[0]);
      }
    } else if (mode === 'create_invitation') {
      // Reset to defaults for new invitation
      resetForm();
      setEmail(initialEmail);
    }
  }, [isOpen, mode, currentPlan, initialEmail]);

  // Set default fixed plan when plans load
  useEffect(() => {
    if (plans.length > 0 && planType === 'fixed' && !fixedPlanCode) {
      const firstFixedPlan = plans.find(p => p.plan_kind === 'fixed');
      if (firstFixedPlan) {
        setFixedPlanCode(firstFixedPlan.plan_code);
      }
    }
  }, [plans, planType, fixedPlanCode]);

  // Filter plans based on selected plan type
  const filteredPlans = useMemo(() => {
    if (planType === 'fixed') {
      return plans.filter(plan => plan.plan_kind === 'fixed');
    } else if (planType === 'payg') {
      return plans.filter(plan => plan.plan_kind === 'payg');
    }
    return plans;
  }, [plans, planType]);

  // Clear fixed plan code when switching away from fixed plans
  useEffect(() => {
    if (planType !== 'fixed') {
      setFixedPlanCode('');
    }
  }, [planType]);

  const handlePlanTypeChange = (newPlanType: PlanType) => {
    setPlanType(newPlanType);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.planType;
      delete next.planId;
      return next;
    });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const endOfDay = `${dateValue}T23:59:59`;
      setExpiresAt(endOfDay);
    } else {
      setExpiresAt('');
    }
  };

  const getInvitationFormState = (): InvitationFormState => ({
    email,
    workspaceMode,
    tenantId,
    tenantName,
    planType,
    fixedPlanCode,
  });

  const handleWorkspaceModeChange = (newMode: WorkspaceMode) => {
    setWorkspaceMode(newMode);
    setFieldErrors({});
    if (newMode !== 'existing_workspace') setTenantId(null);
    if (newMode !== 'custom_workspace') setTenantName('');
    if (newMode === 'existing_workspace') {
      setPlanType('fixed');
      setFixedPlanCode('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'create_invitation') {
      const formState = getInvitationFormState();
      const validation = validateInvitationForm(workspaceMode, formState, plans);
      if (!validation.valid) {
        setFieldErrors(validation.errors);
        return;
      }
      setFieldErrors({});
      const payload = buildInvitationPayload(workspaceMode, formState, plans);
      try {
        await onSubmit(payload);
        setTimeout(() => resetForm(), 300);
      } catch (error) {
        throw error;
      }
      return;
    }

    if (planType === 'fixed' && !fixedPlanCode) return;

    const formData: PlanFormData & { email?: string; userId?: string } = {
      planType,
      fixedPlanCode,
      limits: PLAN_METERS.reduce<PlanLimits>(
        (acc, { key }) => ({ ...acc, [key]: unlimited[key] ? -1 : limits[key] }),
        { ...limits }
      ),
      expiresAt,
    };
    if (mode === 'update_plan' && userId) formData.userId = userId;

    try {
      await onSubmit(formData);
      setTimeout(() => resetForm(), 300);
    } catch (error) {
      throw error;
    }
  };

  const resetForm = () => {
    setPlanType('fixed');
    setFixedPlanCode('');
    setLimits(EMPTY_LIMITS);
    setUnlimited(NO_UNLIMITED);
    setExpiresAt('');
    setEmail('');
    setTenantId(null);
    setWorkspaceMode('new_workspace');
    setTenantName('');
    setFieldErrors({});
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setTimeout(() => resetForm(), 300);
    }
  };

  const modalTitle = mode === 'create_invitation'
    ? 'Create New Invitation and Assign Plan'
    : 'Update Plan';
  const modalDescription = mode === 'create_invitation'
    ? 'Invite a user and configure their workspace access.'
    : userName
      ? `for ${userName} (${userEmail})`
      : 'Update the user\'s plan configuration';

  const invitationFormState = getInvitationFormState();
  const invitationValidation = mode === 'create_invitation'
    ? validateInvitationForm(workspaceMode, invitationFormState, plans)
    : { valid: true, errors: {} as ValidationResult['errors'] };
  const isCreateInvitationValid = mode !== 'create_invitation' || invitationValidation.valid;
  const isSubmitDisabled = isSubmitting || (mode === 'create_invitation' && !isCreateInvitationValid);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-dark-invirtus/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full border border-light-lila/30 animate-in slide-in-from-bottom duration-300 max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-4 py-2 border-b border-dark-invirtus/10 bg-gradient-to-r from-light-lila/20 to-light-purple/10 rounded-t-xl sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-heading font-bold text-dark-invirtus flex items-center gap-2">
                <div className="w-1 h-5 bg-gradient-to-b from-gradient-purple to-gradient-pink rounded-full"></div>
                {modalTitle}
              </h3>
              {modalDescription && (
                <p className="text-sm text-dark-invirtus/70 mt-0.5 font-medium">
                  {modalDescription}
                </p>
              )}
            </div>
            <Button
              type="icon"
              intent="muted"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
              aria-label="Close"
              icon={<X />}
            />
          </div>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-3 space-y-2.5">
          {/* Email Input (create_invitation mode) or Current Plan Card (update_plan mode) */}
          {mode === 'create_invitation' ? (
            <div className="space-y-1">
              <Typography
                as="label"
                htmlFor="invitation-email"
                variant="body-sm"
                weight="semibold"
                color="primary"
                className="flex items-center gap-2"
              >
                Email Address
              </Typography>
              <DSInput
                id="invitation-email"
                type="email"
                placeholder="user@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }));
                }}
                disabled={isSubmitting}
                error={fieldErrors.email}
              />
            </div>
          ) : (
            currentPlan?.has_plan && (
              <div className="bg-gradient-to-r from-blue/10 to-light-blue/20 border border-blue/30 rounded-lg p-2 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue flex-shrink-0 mt-0.5"/>
                <div className="text-sm flex-1">
                  <p className="font-heading font-bold text-dark-invirtus mb-0.5">Current Plan</p>
                  <p className="text-dark-invirtus/80">
                    {currentPlan.plan_kind === 'fixed' && currentPlan.fixed_plan_code
                      ? `${currentPlan.fixed_plan_code.replace('_', ' ').toUpperCase()} (FIXED)`
                      : currentPlan.plan_kind === 'payg'
                      ? 'Pay-As-You-Go (PAYG)'
                      : currentPlan.plan_kind === 'custom'
                      ? 'Enterprise (CUSTOM)'
                      : currentPlan.plan_kind?.toUpperCase() || 'Unknown'
                    }
                    {currentPlan.expires_at && ` • Expires: ${formatDate(currentPlan.expires_at)}`}
                  </p>
                </div>
              </div>
            )
          )}

          {/* Workspace mode (create_invitation only) */}
          {mode === 'create_invitation' && tenants && tenants.length > 0 && (
            <div>
              <Typography variant="body-sm" weight="semibold" color="primary" className="mb-0.5">
                Workspace
              </Typography>
              <div className="flex flex-col gap-1.5">
                <div className="flex gap-2">
                  <Button
                    type="outlined"
                    size="sm"
                    intent={workspaceMode === 'new_workspace' ? 'primary' : 'secondary'}
                    label="New Workspace"
                    disabled={isSubmitting}
                    onClick={() => handleWorkspaceModeChange('new_workspace')}
                  />
                  <Button
                    type="outlined"
                    size="sm"
                    intent={workspaceMode === 'existing_workspace' ? 'primary' : 'secondary'}
                    label="Existing Workspace"
                    disabled={isSubmitting}
                    onClick={() => handleWorkspaceModeChange('existing_workspace')}
                  />
                  <Button
                    type="outlined"
                    size="sm"
                    intent={workspaceMode === 'custom_workspace' ? 'primary' : 'secondary'}
                    label="Custom Workspace"
                    disabled={isSubmitting}
                    onClick={() => handleWorkspaceModeChange('custom_workspace')}
                  />
                </div>

                {workspaceMode === 'existing_workspace' && (
                  <SearchableWorkspaceSelect
                    tenants={tenants}
                    value={tenantId}
                    onChange={(value) => {
                      setTenantId(value);
                      if (fieldErrors.tenantId) setFieldErrors((prev) => ({ ...prev, tenantId: undefined }));
                    }}
                    disabled={isSubmitting}
                    error={fieldErrors.tenantId}
                  />
                )}

                {workspaceMode === 'custom_workspace' && (
                  <div className="space-y-0.5">
                    <DSInput
                      type="text"
                      placeholder="Enter workspace name"
                      value={tenantName}
                      onChange={(e) => {
                        setTenantName(e.target.value);
                        if (fieldErrors.tenantName) setFieldErrors((prev) => ({ ...prev, tenantName: undefined }));
                      }}
                      disabled={isSubmitting}
                      error={fieldErrors.tenantName}
                    />
                  </div>
                )}

                <Typography variant="body-xs" color="muted" className="mt-0.5">
                  {WORKSPACE_HINTS[workspaceMode]}
                </Typography>
              </div>
            </div>
          )}

          {/* Plan Type + Plan list: only for update_plan OR create_invitation with new_workspace/custom_workspace */}
          {(mode === 'update_plan' || (mode === 'create_invitation' && (workspaceMode === 'new_workspace' || workspaceMode === 'custom_workspace'))) && (
          <>
          <div>
            <label className="block text-sm font-heading font-semibold text-dark-invirtus mb-1 flex items-center gap-2">
              Plan Type
            </label>
            {mode === 'create_invitation' && (fieldErrors.planType || fieldErrors.planId) && (
              <p className="text-xs text-red-600 mb-0.5" role="alert">
                {fieldErrors.planType ?? fieldErrors.planId}
              </p>
            )}
            <div className="grid grid-cols-3 gap-2">
              {/* Fixed Plan Card */}
              <button
                type="button"
                onClick={() => handlePlanTypeChange('fixed')}
                disabled={isSubmitting}
                className={`p-2 rounded-lg border-2 transition-all duration-200 text-left ${
                  planType === 'fixed'
                    ? 'border-purple bg-purple/5 shadow-sm'
                    : 'border-light-lila/30 bg-white hover:border-purple/50 hover:bg-light-lila/5'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <div className={`p-1 rounded ${planType === 'fixed' ? 'bg-purple/20' : 'bg-gray-100'}`}>
                    <Target className={`w-3.5 h-3.5 ${planType === 'fixed' ? 'text-purple' : 'text-gray-600'}`} />
                  </div>
                  <span className="font-semibold text-sm text-dark-invirtus">Fixed Plan</span>
                </div>
                <p className="text-xs text-dark-invirtus/60">Predefined limits</p>
              </button>

              {/* PAYG Plan Card */}
              <button
                type="button"
                onClick={() => handlePlanTypeChange('payg')}
                disabled={isSubmitting}
                className={`p-2 rounded-lg border-2 transition-all duration-200 text-left ${
                  planType === 'payg'
                    ? 'border-blue bg-blue/5 shadow-sm'
                    : 'border-light-lila/30 bg-white hover:border-blue/50 hover:bg-blue/5'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <div className={`p-1 rounded ${planType === 'payg' ? 'bg-blue/20' : 'bg-gray-100'}`}>
                    <Zap className={`w-3.5 h-3.5 ${planType === 'payg' ? 'text-blue' : 'text-gray-600'}`} />
                  </div>
                  <span className="font-semibold text-sm text-dark-invirtus">Pay-As-You-Go</span>
                </div>
                <p className="text-xs text-dark-invirtus/60">Unlimited usage</p>
              </button>

              {/* Enterprise Plan Card */}
              <button
                type="button"
                onClick={() => handlePlanTypeChange('enterprise')}
                disabled={isSubmitting}
                className={`p-2 rounded-lg border-2 transition-all duration-200 text-left ${
                  planType === 'enterprise'
                    ? 'border-purple bg-purple/5 shadow-sm'
                    : 'border-light-lila/30 bg-white hover:border-purple/50 hover:bg-light-lila/5'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <div className={`p-1 rounded ${planType === 'enterprise' ? 'bg-purple/20' : 'bg-gray-100'}`}>
                    <Building2 className={`w-3.5 h-3.5 ${planType === 'enterprise' ? 'text-purple' : 'text-gray-600'}`} />
                  </div>
                  <span className="font-semibold text-sm text-dark-invirtus">Enterprise</span>
                </div>
                <p className="text-xs text-dark-invirtus/60">Custom limits</p>
              </button>
            </div>
          </div>

          {/* Plan Selection Based on Type */}
          {planType === 'fixed' ? (
            <div>
              <label className="block text-sm font-heading font-semibold text-dark-invirtus mb-1">
                Select Fixed Plan
              </label>
              <PlanCardSelector
                plans={filteredPlans}
                selectedPlanCode={fixedPlanCode}
                onSelect={(code) => {
                  setFixedPlanCode(code);
                  if (fieldErrors.planId) setFieldErrors((prev) => ({ ...prev, planId: undefined }));
                }}
                disabled={isSubmitting || filteredPlans.length === 0}
                filterCustom={false}
              />
              <p className="mt-1 text-xs text-dark-invirtus/60">
                Plans are loaded from database. Update limits by modifying the database records.
              </p>
            </div>
          ) : planType === 'payg' ? (
            <div className="bg-gradient-to-r from-blue/10 to-light-blue/20 rounded-lg p-3 border border-blue/30">
              <div className="flex items-start gap-2">
                <div className="p-1.5 bg-blue/20 rounded-lg flex-shrink-0">
                  <Zap className="w-4 h-4 text-blue" />
                </div>
                <div>
                  <h4 className="text-sm font-heading font-bold text-dark-invirtus mb-0.5">
                    Unlimited Usage
                  </h4>
                  <p className="text-xs text-dark-invirtus/70 leading-relaxed">
                    Pay-As-You-Go plans have <strong>unlimited</strong> access to all features. 
                    Usage is tracked and billed based on actual consumption.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-r from-purple/10 to-light-purple/20 rounded-lg p-3 border border-purple/30">
                <div className="flex items-start gap-2">
                  <div className="p-1.5 bg-purple/20 rounded-lg flex-shrink-0">
                    <Settings className="w-4 h-4 text-purple" />
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-bold text-dark-invirtus mb-0.5">
                      Custom Configuration
                    </h4>
                    <p className="text-xs text-dark-invirtus/70 leading-relaxed">
                      Enterprise plans allow custom limits and expiration. Set specific limits or enable unlimited access.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {PLAN_METERS.map(({ key, label }) => (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-heading font-semibold text-dark-invirtus">
                        {label}
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={unlimited[key]}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setUnlimited((prev) => ({ ...prev, [key]: checked }));
                            if (checked) setLimits((prev) => ({ ...prev, [key]: 0 }));
                          }}
                          disabled={isSubmitting}
                          className="w-4 h-4 rounded border-gray-300 accent-purple-600 focus:ring-purple-500 cursor-pointer disabled:cursor-not-allowed disabled:accent-gray-300"
                        />
                        <span className="text-xs font-medium text-dark-invirtus/70">Unlimited</span>
                      </label>
                    </div>
                    <DSInput
                      type="number"
                      value={limits[key]}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setLimits((prev) => ({ ...prev, [key]: value }));
                      }}
                      disabled={isSubmitting || unlimited[key]}
                      min={0}
                      placeholder={unlimited[key] ? "Unlimited" : "Enter limit"}
                      rounded="lg"
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Shared Expiration Date Input (for PAYG and Enterprise) */}
          {(planType === 'payg' || planType === 'enterprise') && (
            <div>
              <label className="block text-sm font-heading font-semibold text-dark-invirtus mb-1 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Expiration Date (Optional)
              </label>
              <div className="flex gap-2">
                <div className="min-w-0 flex-1">
                  <DSInput
                    type="date"
                    value={expiresAt ? expiresAt.split('T')[0] : ''}
                    onChange={handleDateChange}
                    disabled={isSubmitting}
                    min={new Date().toISOString().split('T')[0]}
                    rounded="lg"
                  />
                </div>
                {expiresAt && (
                  <Button
                    type="outlined"
                    intent="danger"
                    size="sm"
                    label="Clear"
                    onClick={() => setExpiresAt('')}
                    disabled={isSubmitting}
                    title="Clear date"
                  />
                )}
              </div>
              <p className="mt-1 text-xs text-dark-invirtus/60">
                Optional: Set an expiration date for the {planType === 'payg' ? 'PAYG' : 'Enterprise'} plan (expires at 23:59)
              </p>
            </div>
          )}
          </>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-light-lila/30">
            <Button
              type="outlined"
              intent="secondary"
              size="sm"
              label="Cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            />
            <Button
              htmlType="submit"
              type="contained"
              intent="secondary"
              size="sm"
              label={mode === 'create_invitation' ? 'Create Invitation' : 'Save Plan'}
              loading={isSubmitting}
              disabled={isSubmitDisabled}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

